import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const CommentsListingPage = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [commentInteractions, setCommentInteractions] = useState({}); // Track likes for each comment

  // Mock data for now - will be replaced with API call
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const mockComments = [
        {
          id: 1,
          address: '123 Main St, New York, NY 10001',
          commentText: 'This neighborhood has been great for construction projects. Good access to materials and suppliers.',
          commenter: 'John Smith',
          role: 'Site Supervisor',
          date: '2024-01-15',
          category: 'Construction',
          searchType: 'Street',
          avatar: '👷'
        },
        {
          id: 2,
          address: '90210 Zipcode Area, Los Angeles, CA',
          commentText: 'Overall good area for renovation work. Some traffic congestion during peak hours.',
          commenter: 'Sarah Johnson',
          role: 'Project Manager',
          date: '2024-01-10',
          category: 'Renovation',
          searchType: 'Zipcode',
          avatar: '👩‍💼'
        },
        {
          id: 3,
          address: '789 Pine Rd, Chicago, IL 60601',
          commentText: 'Excellent area for commercial construction. Permits are processed quickly here.',
          commenter: 'Mike Wilson',
          role: 'Contractor',
          date: '2024-01-08',
          category: 'Commercial',
          searchType: 'Street',
          avatar: '👨‍🔧'
        },
        {
          id: 4,
          address: '60601 Zipcode Area, Chicago, IL',
          commentText: 'Mixed reviews for this zipcode. Some areas are great, others have permit delays.',
          commenter: 'Lisa Brown',
          role: 'Architect',
          date: '2024-01-05',
          category: 'Architecture',
          searchType: 'Zipcode',
          avatar: '👩‍🎨'
        }
      ];
      setComments(mockComments);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredComments = comments.filter(comment => {
    if (filter === 'all') return true;
    if (filter === 'street') return comment.searchType === 'Street';
    if (filter === 'zipcode') return comment.searchType === 'Zipcode';
    return true;
  });

  const handleLike = (commentId) => {
    setCommentInteractions(prev => ({
      ...prev,
      [commentId]: {
        ...prev[commentId],
        liked: !prev[commentId]?.liked,
        likes: prev[commentId]?.liked ? (prev[commentId]?.likes || 1) - 1 : (prev[commentId]?.likes || 0) + 1
      }
    }));
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading comments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comments-listing-page">
      <Navbar />
      <div className="container mt-4 mb-5">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div>
            <h1 className="mb-2">Comments for 123 Main St, New York, NY 10001</h1>
            <p className="text-muted mb-0">Browse through all contractor comments and insights for this address</p>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Filter Comments</h5>
              <div className="d-flex flex-wrap gap-2">
                <button 
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All Comments ({comments.length})
                </button>
                <button 
                  className={`btn ${filter === 'street' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('street')}
                >
                  Street Comments ({comments.filter(c => c.searchType === 'Street').length})
                </button>
                <button 
                  className={`btn ${filter === 'zipcode' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('zipcode')}
                >
                  Zipcode Comments ({comments.filter(c => c.searchType === 'Zipcode').length})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="row">
        <div className="col-12">
          {filteredComments.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-muted">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h4>No comments found</h4>
              <p className="text-muted">No comments match the selected filter criteria.</p>
            </div>
          ) : (
            <div className="row">
              {filteredComments.map((comment) => (
                <div key={comment.id} className="col-12 mb-3">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body py-3">
                      <div className="row g-0">
                        {/* Comment Text - Full Row */}
                        <div className="col-12 mb-3">
                          <div className="comment-text-container">
                            <p className="comment-text mb-0">{comment.commentText}</p>
                          </div>
                        </div>

                        {/* Search Type and Date - Second Row */}
                        <div className="col-12">
                          <div className="row align-items-center g-0">
                            <div className="col-md-6">
                              {/* Search Type Badge */}
                              <div className="d-flex align-items-center">
                                <div className={`search-type-badge ${comment.searchType === 'Street' ? 'street-badge' : 'zipcode-badge'}`}>
                                  <span className="search-type-icon me-2">
                                    {comment.searchType === 'Street' ? '🏘️' : '📍'}
                                  </span>
                                  <span className="search-type-text">{comment.searchType}</span>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-6">
                              {/* Date of Comment */}
                              <div className="text-end">
                                <div className="text-muted small mb-1">Commented on</div>
                                <div className="fw-semibold text-dark">{moment(comment.date).format('MMM DD, YYYY')}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Like Button */}
                        <div className="row mt-3">
                          <div className="col-12">
                            <div className="interaction-buttons border-top pt-3">
                              <button 
                                className={`interaction-btn like-btn ${commentInteractions[comment.id]?.liked ? 'active' : ''}`}
                                onClick={() => handleLike(comment.id)}
                                title="Like this comment"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 2.9987C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                </svg>
                                <span>Like</span>
                                {commentInteractions[comment.id]?.likes > 0 && (
                                  <span className="interaction-count">({commentInteractions[comment.id]?.likes})</span>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default CommentsListingPage; 